import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function Turnovers() {
  const { user, isViewer } = useAuth();
  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ property_id: "", title: "", move_out_date: "", target_ready_date: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target?.value ?? e });

  const load = () => api.get("/turnovers").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/properties").then(({ data }) => setProps(data)); }, []);

  const create = async () => {
    if (!f.property_id || !f.title) return toast.error("Fill required fields");
    try { await api.post("/turnovers", f); toast.success("Turnover created"); setOpen(false); load(); } catch (e) { toast.error("Failed"); }
  };
  const toggle = async (tid, task_id, done) => { try { await api.put(`/turnovers/${tid}/task`, { task_id, done }); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <DashboardLayout nav={getNav(user.role)} title="Property Turnovers">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-turnover-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> New Turnover</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-head">New Turnover Project</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Property</Label><Select value={f.property_id} onValueChange={set("property_id")}><SelectTrigger data-testid="turn-property" className="mt-1.5"><SelectValue placeholder="Select property" /></SelectTrigger><SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Title</Label><Input data-testid="turn-title" value={f.title} onChange={set("title")} className="mt-1.5" placeholder="Unit 4B Turnover" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Move-Out Date</Label><Input data-testid="turn-moveout" type="date" value={f.move_out_date} onChange={set("move_out_date")} className="mt-1.5" /></div>
                <div><Label>Target Ready</Label><Input data-testid="turn-ready" type="date" value={f.target_ready_date} onChange={set("target_ready_date")} className="mt-1.5" /></div>
              </div>
              <Button data-testid="turn-save" onClick={create} className="w-full bg-blue-600 hover:bg-blue-700">Create Turnover</Button>
            </div>
          </DialogContent>
        </Dialog>}
      </div>
      {!items ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        items.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-slate-200"><ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No turnover projects yet.</p></div> : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((t) => {
              const done = t.tasks.filter((x) => x.done).length;
              return (
                <div key={t.id} data-testid={`turnover-${t.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-head font-semibold text-slate-900">{t.title}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.status === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{t.status}</span>
                  </div>
                  <p className="text-sm text-slate-500">{t.property_name} · {done}/{t.tasks.length} tasks</p>
                  <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${(done / t.tasks.length) * 100}%` }} /></div>
                  <div className="mt-3 space-y-1.5">
                    {t.tasks.map((tk) => (
                      <label key={tk.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" data-testid={`turn-task-${tk.id}`} checked={tk.done} disabled={isViewer} onChange={(e) => toggle(t.id, tk.id, e.target.checked)} className="rounded" />
                        <span className={tk.done ? "line-through text-slate-400" : ""}>{tk.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </DashboardLayout>
  );
}
